import { Flex, Box } from '@chakra-ui/react';
import { Avatar, AvatarFallback, AvatarImage, Card, CardContent } from '@mesh.ai/rabbit';
import { ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';

import { TextLabel, TextLabelSmall, TextBodyMedium } from 'uiToolkitV1/Typography';

const getGuidelineColor = ({ y: incrementPercentage }) => {
  // TODO: fetch the value of guidelines dynamically from the backend
  if (incrementPercentage >= 2 && incrementPercentage <= 4) {
    return '#1a5724';
  }
  return '#C62C02';
};

// Mock data for demonstration
const performanceData = [
  {
    x: 1.2,
    y: 1,
    profile: {
      name: 'Sibley Bourn',
      email: 'sibley.bourn@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50695/2023/01/06/50695.jpg_1673027621626',
    },
  },
  {
    x: 2.4,
    y: 2,
    profile: {
      name: 'Yevgeniya Vinogradova',
      email: 'yevgeniya.vinogradova@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50707/2023/01/06/50707.jpg_1673027634925',
    },
  },
  {
    x: 3,
    y: 3,
    profile: {
      name: 'Nikhil Bhat',
      email: 'nikhil.bhat@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50703/2023/01/06/50703.jpg_1673027608662',
    },
  },
  {
    x: 1.6,
    y: 4,
    profile: {
      name: 'Carlos Jiron',
      email: 'carlos.jiron@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50700/2023/01/06/50700.jpg_1673027566718',
    },
  },
  {
    x: 2.8,
    y: 5,
    profile: {
      name: 'Chloe Nunez',
      email: 'chloe.nunez@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50721/2023/01/06/50721.jpg_1673027588228',
    },
  },
  {
    x: 2.8,
    y: 7,
    profile: {
      name: 'Dai Long',
      email: 'dai.long@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50715/2023/01/06/50715.jpg_1673027571813',
    },
  },
  {
    x: 1,
    y: 9,
    profile: {
      name: 'Ged Frost',
      email: 'ged.frost@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50709/2023/01/06/50709.jpg_1673027575066',
    },
  },
  {
    x: 2,
    y: 10,
    profile: {
      name: 'Giulia Mancini',
      email: 'giulia.mancini@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50714/2023/01/06/50714.jpg_1673027577544',
    },
  },
  {
    x: 3,
    y: 12,
    profile: {
      name: 'Hattie Cooper',
      email: 'hattie.cooper@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50702/2023/01/06/50702.jpg_1673027580262',
    },
  },
  {
    x: 1,
    y: 8,
    profile: {
      name: 'George Weber',
      email: 'george.weber@abc.co',
      profilePhoto: 'https://media.mesh.ai/2415/media/profilePhoto/50720/2023/01/06/50720.jpg_1673027560850',
    },
  },
];

const RenderAvatar = ({ cx, cy, payload }) => {
  const tx = cx - 8;
  const ty = cy - 8;
  const {
    profile: { profilePhoto },
  } = payload || {};

  const borderColor = getGuidelineColor(payload);

  return (
    <g>
      {/* Border */}
      <circle cx={cx} cy={cy} r="8" fill="none" stroke={borderColor} strokeWidth="5px" />
      <image
        x={tx}
        y={ty}
        width={16}
        height={16}
        xlinkHref={profilePhoto}
        clipPath="inset(0% round 50%)"
        preserveAspectRatio="xMidYMid slice"
      />
    </g>
  );
};

const RenderTooltip = (tooltipData) => {
  const { payload } = tooltipData;
  const name = payload[0]?.payload?.profile.name;
  const profilePhoto = payload[0]?.payload?.profile.profilePhoto;
  const email = payload[0]?.payload?.profile.email;
  const avatarFallback = name?.substring(0, 2);
  const payChange = payload[0]?.payload?.y;
  const performanceRating = payload[0]?.payload?.x;
  const guidelineColor = getGuidelineColor({ y: payChange });

  return (
    <Card
      style={{
        minWidth: '224px',
        padding: '8px',
      }}
    >
      <CardContent style={{ padding: 0 }}>
        <Flex gap="8px">
          {/* Green vertical bar */}
          <Box w="3px" bg={guidelineColor} borderRadius="full" />

          <Flex direction="column" flex="1">
            {/* Performance rating section */}
            <Flex direction="column" gap="8px" p="8px 8px 16px 8px">
              <Flex direction="column" gap="4px">
                <Flex direction="column" gap="4px" p="4px">
                  <TextLabelSmall color="text.g2">Performance rating</TextLabelSmall>
                  <TextBodyMedium bold color="text.g1">
                    {performanceRating}
                  </TextBodyMedium>
                </Flex>
              </Flex>

              {/* Pay change section */}
              <Flex direction="column" gap="4px">
                <Flex direction="column" gap="4px" p="4px">
                  <TextLabelSmall color="text.g2">Pay change</TextLabelSmall>
                  <TextBodyMedium bold color={guidelineColor}>
                    {payChange}%
                  </TextBodyMedium>
                </Flex>
              </Flex>

              {/* Guidelines section */}
              <Flex direction="column" gap="4px">
                <Flex direction="column" gap="4px" p="4px">
                  <TextLabelSmall color="text.g2">Guidelines</TextLabelSmall>
                  <TextBodyMedium bold color="text.g1">
                    2%{' '}
                    <TextBodyMedium as="span" color="text.g2">
                      to
                    </TextBodyMedium>{' '}
                    4%
                  </TextBodyMedium>
                </Flex>
              </Flex>
            </Flex>

            {/* Horizontal divider */}
            <Box h="1px" bg="neutral.CCCCCC" w="100%" />

            {/* Employee information section */}
            <Flex alignItems="center" gap="8px" p="8px 12px 4px 12px">
              <Box boxSize="24px">
                <Avatar size="sm">
                  <AvatarImage src={profilePhoto} alt="Avatar" />
                  <AvatarFallback>
                    <TextLabelSmall>{avatarFallback}</TextLabelSmall>
                  </AvatarFallback>
                </Avatar>
              </Box>
              <Flex direction="column" flex="1">
                <TextLabel color="text.g1">{name}</TextLabel>
                <TextLabel color="text.g2">{email}</TextLabel>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};

const RenderLegend = () => (
  <Flex justifyContent="flex-end" gap="6px" position="absolute" bottom="0" left="0" right="0">
    <Flex alignItems="center" gap="4px" padding="0px 4px">
      <svg width="12" height="12">
        <circle cx="6" cy="6" r="4" fill="none" stroke="#1a5724" strokeWidth="2" />
      </svg>
      <TextLabel color="text.g2">Within guidelines</TextLabel>
    </Flex>
    <Flex alignItems="center" gap="4px" padding="0px 4px">
      <svg width="12" height="12">
        <circle cx="6" cy="6" r="4" fill="none" stroke="#C62C02" strokeWidth="2" />
      </svg>
      <TextLabel color="text.g2">Outside guidelines</TextLabel>
    </Flex>
  </Flex>
);

export const PerformanceRatingScatterChart = () => (
  <Card style={{ height: '100%', padding: '12px', boxShadow: 'none' }}>
    <CardContent>
      <Flex
        w="100%"
        h="100%"
        alignItems="center"
        flexDirection="column"
        position="relative"
        sx={{ '.recharts-tooltip-wrapper': { zIndex: 1 } }}
      >
        <ResponsiveContainer width="100%" height={222}>
          <ScatterChart width={400} height={222} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
            {/* X-Axis: Performance rating */}
            <XAxis
              type="number"
              dataKey="x"
              name="Performance rating"
              axisLine
              tickLine={false}
              label={{
                value: 'Performance rating →',
                position: 'insideBottom',
                offset: -10,
                fill: '#666666',
                style: { textAnchor: 'middle', fontWeight: 'bold', fontSize: '10px', lineHeight: '16px' },
              }}
              domain={[0.6, 3.2]}
              ticks={[1, 2, 3]}
              tickFormatter={(tick) => {
                if (tick === 1) return 'Needs improvement';
                if (tick === 2) return 'Exceeds expectations';
                if (tick === 3) return 'Outstanding';
                return tick;
              }}
              tick={{
                fill: '#666666',
                fontWeight: 'bold',
                fontSize: '10px',
              }}
            />
            {/* Y-Axis: Pay change % */}
            <YAxis
              type="number"
              dataKey="y"
              axisLine
              tickLine={false}
              label={{
                value: 'Pay change (%) →',
                angle: -90,
                position: 'insideLeft',
                offset: 20,
                fill: '#666666',
                style: { textAnchor: 'middle', fontWeight: 'bold', fontSize: '10px', lineHeight: '16px' },
              }}
              domain={[0, 12]}
            />
            <Tooltip cursor={false} content={RenderTooltip} />
            {/* Reference Lines for grid */}
            <ReferenceLine x={2} stroke="#EBEFF3" />
            <ReferenceLine y={6} stroke="#EBEFF3" />
            {/* Scatter plot data */}
            <Scatter name="Employees" data={performanceData} fill="#8884d8" shape={RenderAvatar} />
          </ScatterChart>
        </ResponsiveContainer>
        <RenderLegend />
      </Flex>
    </CardContent>
  </Card>
);
